import { format, isSameDay, addDays } from "date-fns";

// ── Invoice turnaround: ONE figure, used everywhere ──────────────────────────
//
// The screen previously promised "24 hours", "24 business hours" and "4 business
// hours" at the same time, in three places. Everything now derives from the
// constant below, so the promise cannot drift again.
//
// ASSUMPTION worth checking: business hours are taken to be Mon–Fri 09:00–18:00
// in the VIEWER's timezone, because nothing in the codebase records Cadomation's
// support hours or timezone. A customer in another timezone is therefore quoted
// a time that reads naturally to them but may not match when the team is
// actually at a desk. If real support hours differ, change only these three
// constants.
export const SLA_BUSINESS_HOURS = 4;
export const BUSINESS_DAY_START = 9;
export const BUSINESS_DAY_END = 18;

/** "4 business hours" — the fallback wording when no clock time can be shown. */
export const SLA_LABEL = `${SLA_BUSINESS_HOURS} business hours`;

const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

const at = (d, hour) => {
  const x = new Date(d);
  x.setHours(hour, 0, 0, 0);
  return x;
};

const nextDayStart = (d) => at(addDays(d, 1), BUSINESS_DAY_START);

/**
 * Walk `hours` forward through business time only, skipping evenings and
 * weekends. A request at 4pm Friday lands Monday morning, not Friday night.
 */
export function addBusinessHours(from, hours = SLA_BUSINESS_HOURS) {
  let remaining = hours * 60;
  let cur = new Date(from);
  if (Number.isNaN(cur.getTime())) return null;

  // Bounded so a bad constant can never spin the render thread.
  let guard = 0;
  while (remaining > 0 && guard++ < 400) {
    if (isWeekend(cur)) {
      cur = nextDayStart(cur);
      continue;
    }
    const dayStart = at(cur, BUSINESS_DAY_START);
    const dayEnd = at(cur, BUSINESS_DAY_END);

    if (cur < dayStart) cur = dayStart;
    if (cur >= dayEnd) {
      cur = nextDayStart(cur);
      continue;
    }

    const availableMins = (dayEnd - cur) / 60000;
    if (availableMins >= remaining) {
      cur = new Date(cur.getTime() + remaining * 60000);
      remaining = 0;
    } else {
      remaining -= availableMins;
      cur = nextDayStart(cur);
    }
  }
  return cur;
}

/** Round up to the next quarter hour, so we never promise earlier than the SLA. */
function roundUpQuarter(d) {
  const x = new Date(d);
  const mins = x.getMinutes();
  const bump = (15 - (mins % 15)) % 15;
  if (bump) x.setMinutes(mins + bump);
  x.setSeconds(0, 0);
  return x;
}

/** When the invoice is due, given when the request was made. */
export function slaTarget(requestedAt = new Date()) {
  const from = new Date(requestedAt);
  if (Number.isNaN(from.getTime())) return null;
  const target = addBusinessHours(from, SLA_BUSINESS_HOURS);
  return target ? roundUpQuarter(target) : null;
}

/**
 * "3:00 PM today" / "9:30 AM tomorrow" / "3:00 PM on Mon 8 Sep".
 * A clock time reassures more than a duration and stays honest at weekends.
 */
export function formatSlaTime(target, now = new Date()) {
  if (!target) return null;
  const t = new Date(target);
  if (Number.isNaN(t.getTime())) return null;

  const time = format(t, "h:mm a");
  if (isSameDay(t, now)) return `${time} today`;
  if (isSameDay(t, addDays(now, 1))) return `${time} tomorrow`;
  return `${time} on ${format(t, "EEE d MMM")}`;
}

/** Clock time where one can be computed, else the plain SLA wording. */
export function slaCopy(requestedAt = new Date(), now = new Date()) {
  return formatSlaTime(slaTarget(requestedAt), now) || `within ${SLA_LABEL}`;
}
